// File: src/features/variant_workflow/molecular_impact/hooks/useSilicoPredictionToolEditModalLogic.ts
import { notification } from 'antd';
import { EditorState } from 'draft-js';
import { useEffect, useMemo, useState } from 'react';

import {
  editorStateToHtml,
  htmlToEditorState,
} from '../../../../foundation/components/rich_text_editor/RichTextEditor';
import { useModalForm } from '../../../../foundation/hooks';
import { displayUnAuthorizedNotification } from '../../../../foundation/utils/notifications';
import { useGetPredictionToolsQuery } from '../../../api/globalDataApi';
import {
  useEditSpliceAiPredictionToolMutation,
  useGetSpliceSiteAlterationQuery,
  useUpdateVariantPredictionToolMutation,
} from '../../../api/variantApi';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../../constants';
import { SilicoPredictionType } from '../../redux';
import {
  ALLOWED_PREDICTION_RESULT_SELECT_TOOLS,
  buildPredictionResultChoices,
  findToolByName,
  selectLabelFilterOption,
} from '../../utils';

interface UseSilicoPredictionToolEditModalLogicProps {
  variant_id: string;
  open: boolean;
  onCancel: () => void;
  tool: SilicoPredictionType & { id?: number };
}

/**
 * Custom hook that encapsulates all the business logic for SilicoPredictionToolEditModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useSilicoPredictionToolEditModalLogic = ({
  variant_id,
  open,
  onCancel,
  tool,
}: UseSilicoPredictionToolEditModalLogicProps) => {
  const {
    isLoading: isModalLoading,
    setIsLoading: setIsModalLoading,
    user,
    stageInfo,
    getIsSaveDisabled,
    createModalOpts,
  } = useModalForm({
    onClose: onCancel,
    successMessage: NOTIFICATION_MESSAGES.PREDICTION_TOOL_UPDATED,
    errorMessage: NOTIFICATION_MESSAGES.PREDICTION_TOOL_UPDATE_FAILED,
    stageType: VARIANT_STAGE_TYPES.MOLECULAR_IMPACT,
  });

  const { data: predictionTools = [] } = useGetPredictionToolsQuery();

  // Mutation hooks return [trigger, result] tuples
  const [editSpliceAiPredictionToolMutation] =
    useEditSpliceAiPredictionToolMutation();
  const [updateVariantPredictionToolMutation] =
    useUpdateVariantPredictionToolMutation();

  // Query result objects expose a refetch() function for manual refresh
  const { refetch: refetchSpliceSiteAlteration } =
    useGetSpliceSiteAlterationQuery({ variant_id }, { skip: !variant_id });

  const modalOpts = {
    ...createModalOpts(`Edit in silico prediction tool entry for ${tool.tool}`),
    open,
  };

  // initialize editor state for comment field (from raw backend html)
  const initialEditorState = useMemo(
    () => htmlToEditorState(tool.comment || ''),
    [tool.comment],
  );
  const [commentEditorState, setCommentEditorState] =
    useState<EditorState>(initialEditorState);
  // Always use the sanitized HTML produced by the editor for initial value
  const initialCommentHtml = useMemo(
    () => editorStateToHtml(initialEditorState),
    [initialEditorState],
  );

  // Derive tool definition from global tools by name
  const currentToolDef = useMemo(
    () => findToolByName(predictionTools as any, tool.tool),
    [predictionTools, tool.tool],
  );

  // Build prediction_result choices from choices_json sorted by `order`
  const predictionResultChoices = useMemo(
    () =>
      buildPredictionResultChoices((currentToolDef as any)?.choices_json || []),
    [currentToolDef],
  );

  const shouldUsePredictionResultSelect = useMemo(
    () =>
      !!currentToolDef &&
      ALLOWED_PREDICTION_RESULT_SELECT_TOOLS.has(
        (currentToolDef as any).name,
      ) &&
      predictionResultChoices.length > 0,
    [currentToolDef, predictionResultChoices],
  );

  // keep editor state in sync when tool changes
  useEffect(() => {
    setCommentEditorState(initialEditorState);
  }, [initialEditorState]);

  const initialValues = {
    score: tool.score?.toString() || '',
    prediction_result:
      tool.tool === 'SpliceAI'
        ? tool.splice_site_alteration || ''
        : tool.prediction || '',
    // Use sanitized HTML string produced by the editor
    comment: initialCommentHtml,
  };

  const handleSubmit = async (values: any) => {
    if (!user) {
      return displayUnAuthorizedNotification();
    }
    if (!tool.id) {
      return notification.error({
        message: NOTIFICATION_MESSAGES.ERROR,
        description: 'Tool entry ID is missing',
      });
    }

    try {
      setIsModalLoading(true);

      const commentHtml = values.comment || null;

      if (tool.tool === 'SpliceAI') {
        await editSpliceAiPredictionToolMutation({
          tool_id: tool.id,
          // Pass variant_id so invalidatesTags can derive the cache key from the arg
          variant_id,
          requestData: {
            score: values.score || null,
            prediction_result: values.prediction_result || null,
            comment: commentHtml,
          },
        }).unwrap();

        // SpliceAI scores feed the splice-site alteration view; refetch it
        // manually since that query isn't tag-invalidated by this mutation
        await refetchSpliceSiteAlteration();
      } else {
        await updateVariantPredictionToolMutation({
          tool_id: tool.id,
          // Pass variant_id so invalidatesTags can derive the cache key from the arg
          variant_id,
          requestData: {
            score: values.score || null,
            prediction_result: values.prediction_result || null,
            comment: commentHtml,
            model_type: tool.model_type,
          },
        }).unwrap();
      }

      // The mutations' invalidatesTags mark VariantPredictions stale, so the
      // affected query refetches automatically

      notification.success({
        message: NOTIFICATION_MESSAGES.SUCCESS,
        description: 'Tool updated successfully.',
      });

      onCancel();
    } catch (error) {
      // BIOFRO-80 ABS review: do NOT call notification.error() here.
      // The Axios response interceptor already shows a toast on every
      // failed request; a second one would duplicate the UI feedback.
      console.error(error);
    } finally {
      setIsModalLoading(false);
    }
  };

  return {
    isModalLoading,
    user,
    stageInfo,
    commentEditorState,
    setCommentEditorState,
    predictionResultChoices,
    shouldUsePredictionResultSelect,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    handleSubmit,
    selectLabelFilterOption,
  };
};

export default useSilicoPredictionToolEditModalLogic;
